import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AdminAuthProvider, useAdminAuth } from "@/context/AdminAuth";
import { PortalAuthProvider, usePortalAuth } from "@/context/PortalAuth";

import Landing from "@/pages/Landing";
import ComingSoon from "@/pages/ComingSoon";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminSocieties from "@/pages/admin/AdminSocieties";
import SocietyOnboard from "@/pages/admin/SocietyOnboard";
import SocietyDetail from "@/pages/admin/SocietyDetail";
import PortalLogin from "@/pages/portal/PortalLogin";
import PortalDashboard from "@/pages/portal/PortalDashboard";
import PortalInvoices from "@/pages/portal/PortalInvoices";
import PortalNotices from "@/pages/portal/PortalNotices";
import PortalComplaints from "@/pages/portal/PortalComplaints";
import PortalApplications from "@/pages/portal/PortalApplications";
import PortalFacilities from "@/pages/portal/PortalFacilities";
import CommitteeDashboard from "@/pages/portal/CommitteeDashboard";

function RequireAdmin({ children }) {
  const { user, loading } = useAdminAuth();
  if (loading) return <FullScreenLoader />;
  if (!user) return <Navigate to="/admin/login" replace />;
  return children;
}

function RequireMember({ children }) {
  const { me, loading } = usePortalAuth();
  if (loading) return <FullScreenLoader />;
  if (!me || !me.person) return <Navigate to="/" replace />;
  return children;
}

function FullScreenLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-sand-50">
      <div className="text-stone-500 editorial text-lg">Loading…</div>
    </div>
  );
}

/**
 * Public root: shows the Coming-Soon gate by default.
 * Insider bypass: append `?insider=1` to unlock the real Landing on this device
 * (persisted in localStorage). Use `?insider=0` to re-lock.
 */
function PublicRoot() {
  const params = new URLSearchParams(window.location.search);
  const q = params.get("insider");
  if (q === "1") { try { localStorage.setItem("tso_insider", "1"); } catch (e) {} }
  if (q === "0") { try { localStorage.removeItem("tso_insider"); } catch (e) {} }
  let insider = false;
  try { insider = localStorage.getItem("tso_insider") === "1"; } catch (e) {}
  return insider ? <Landing /> : <ComingSoon />;
}

export default function App() {
  return (
    <BrowserRouter>
      <AdminAuthProvider>
        <PortalAuthProvider>
          <Toaster richColors position="top-right" />
          <Routes>
            <Route path="/" element={<PublicRoot />} />
            <Route path="/site" element={<Landing />} />

            {/* Portal Owner (Platform Admin) */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<RequireAdmin><AdminDashboard /></RequireAdmin>} />
            <Route path="/admin/societies" element={<RequireAdmin><AdminSocieties /></RequireAdmin>} />
            <Route path="/admin/societies/new" element={<RequireAdmin><SocietyOnboard /></RequireAdmin>} />
            <Route path="/admin/societies/:id" element={<RequireAdmin><SocietyDetail /></RequireAdmin>} />

            {/* Society Member Portal */}
            <Route path="/portal/:slug/login" element={<PortalLogin />} />
            <Route path="/portal/:slug" element={<RequireMember><PortalDashboard /></RequireMember>} />
            <Route path="/portal/:slug/invoices" element={<RequireMember><PortalInvoices /></RequireMember>} />
            <Route path="/portal/:slug/notices" element={<RequireMember><PortalNotices /></RequireMember>} />
            <Route path="/portal/:slug/complaints" element={<RequireMember><PortalComplaints /></RequireMember>} />
            <Route path="/portal/:slug/applications" element={<RequireMember><PortalApplications /></RequireMember>} />
            <Route path="/portal/:slug/facilities" element={<RequireMember><PortalFacilities /></RequireMember>} />
            <Route path="/portal/:slug/committee" element={<RequireMember><CommitteeDashboard /></RequireMember>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </PortalAuthProvider>
      </AdminAuthProvider>
    </BrowserRouter>
  );
}
